import { loadOfflineAudioData } from '@/audio/OfflineAudioLoader'
import { Ref, readonly, ref } from 'vue'
import GtmHelper from '@/analytics/gtm-helper'
import AudioIntensityAnalyzer from '@/audio/AudioIntensityAnalyzer'
import { AudioLoadState, OfflineAudioData } from '@/audio/types'
import { convertBinaryStringToUint8Array } from '@/utils/array-utils'
import { useFileValidator } from '@/composables/UseFileValidator'
/**
 * The audio graph is as follows:
 *
 * Buffer source -> analyser -> mute -> gain -> out
 */

const audioContext = new AudioContext()
let timeUpdateHandle: NodeJS.Timeout
let currentTimeOffsetSeconds = 0
let audioBuffer: AudioBuffer | null = null
const audioBinary = ref<Uint8Array | null>(null)
let audioBufferSourceNode: AudioBufferSourceNode | null = null
let offlineAudioData: OfflineAudioData | null = null
const muteNode = audioContext.createGain()
const gainNode = audioContext.createGain()
// value of the gain for the volume and slider.
let refGainNodeValue: Ref<number>
const audioIntensityAnalyzer = new AudioIntensityAnalyzer()

const audioBackgroundIntensity = ref(0)
const isPlaying = ref(false)
const isMuted = ref(false)
const fileName = ref('')
const loadState = ref<AudioLoadState>('unloaded')

const TIME_UPDATE_INTERVAL_MS = 20
const untrimmedAudioDurationSeconds = ref(0)
const audioDurationSeconds = ref(0)
const audioCurrentTimeSeconds = ref(0)
const loopStartTime = ref(0)
const loopEndTime = ref(0)
const audioBufferWaveformData = ref<Float32Array>()

const updateTime = (): void => {
  audioCurrentTimeSeconds.value = getAudioContextCurrentTime()
  timeUpdateHandle = setTimeout(updateTime.bind(this), TIME_UPDATE_INTERVAL_MS)
}

const getAudioContextCurrentTime = (): number => {
  if (isPlaying.value && audioBufferSourceNode?.buffer) {
    return (audioContext.currentTime - currentTimeOffsetSeconds) % audioDurationSeconds.value
  }

  return 0
}

const seekAudio = (time: number) => {
  time += loopStartTime.value
  playAudio(time)
}

const playAudio = (time = 0): void => {
  audioBufferSourceNode?.disconnect()
  audioBufferSourceNode = audioContext.createBufferSource()
  audioBufferSourceNode.buffer = audioBuffer

  audioBufferSourceNode.loop = true
  setBufferNodeLoopTimes()
  audioDurationSeconds.value = loopEndTime.value - loopStartTime.value

  audioBufferSourceNode.connect(muteNode)
  audioBufferSourceNode.start(0, time)
  currentTimeOffsetSeconds = audioContext.currentTime - time + loopStartTime.value
  isPlaying.value = true
  // If audio autoplaying is blocked, mute the audio
  if (audioContext.state === 'suspended') {
    muteAudio()
    isPlaying.value = false
  }
}

const stopAudio = (): void => {
  if (audioBufferSourceNode) {
    audioBufferSourceNode.disconnect()
    audioBufferSourceNode = null
  }
  isPlaying.value = false
}

const muteAudio = (): void => {
  muteNode.gain.value = 0
  isMuted.value = true
}

const unmuteAudio = (): void => {
  muteNode.gain.value = 1
  isMuted.value = false
}

const unloadAudio = (): void => {
  clearTimeout(timeUpdateHandle)
  stopAudio()
  loadState.value = 'unloaded'
}

/**
 * For cases when audioContext is not running due to browser autoplay policies requiring user interaction to trigger the
 * start of the context.
 * Must be called on a user input event.
 */
const unlockAudioContext = (): void => {
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(() => {
      isPlaying.value = true
    })
  }
}

const { validateAudioFileExtension, validateAudioFileLength } = useFileValidator()

const addAudioFile = async (file: File): Promise<void> => {
  try {
    loadState.value = 'loading'
    // reset offlineAudioData here so that the audioReactivity stops immediately.
    offlineAudioData = null
    const audioBuffer = await loadAudioFile(file)
    offlineAudioData = await loadOfflineAudioData(audioBuffer)
    initAudioBackgroundIntensity(offlineAudioData.frequencyData)
  }
  catch (e) {
    loadState.value = 'error'
    throw e
  }
}

const loadAudioURL = async (fileURL: string, fileName: string) => {
  try {
    loadState.value = 'loading'
    const response = await fetch(fileURL)
    const blob = await response.blob()
    const file = new File([blob], fileName, { type: blob.type })
    await addAudioFile(file)
  }
  catch (e) {
    loadState.value = 'error'
    throw e
  }
}

const loadAudioFile = async (file: File): Promise<AudioBuffer> => {
  const arrayBuffer = await file.arrayBuffer()
  const fileReader = new FileReader()
  fileReader.addEventListener('load', (e) => {
    if (!e.target?.result) {
      throw new Error('failed to read audio file')
    }
    if (e.target.result && e.target.result instanceof ArrayBuffer) {
      audioBinary.value = new Uint8Array(e.target.result)
    } else {
      audioBinary.value = convertBinaryStringToUint8Array(e.target.result)
    }
  })

  validateAudioFileExtension(file)
  const decodedAudioBuffer = await audioContext.decodeAudioData(arrayBuffer)
  validateAudioFileLength(file, decodedAudioBuffer.duration)
  fileReader.readAsBinaryString(file)

  fileName.value = file.name
  audioDurationSeconds.value = decodedAudioBuffer.duration
  untrimmedAudioDurationSeconds.value = decodedAudioBuffer.duration
  loopStartTime.value = 0
  loopEndTime.value = audioDurationSeconds.value
  audioBuffer = decodedAudioBuffer
  audioBufferWaveformData.value = audioBuffer.getChannelData(0)
  updateTime()
  loadState.value = 'loaded'

  GtmHelper.getInstance().triggerAudioFileUploadGtmEvents(decodedAudioBuffer.duration, file.size)

  return audioBuffer
}

const initAudioBackgroundIntensity = (audioData: Uint8Array[]): void => {
  audioBackgroundIntensity.value = audioIntensityAnalyzer.getBackgroundNoiseIntensity(audioData)
}

const getAudioDataAtTime = (timeSeconds: number): Uint8Array | null => {
  if (offlineAudioData && audioBuffer) {
    return offlineAudioData.frequencyData[Math.floor(timeSeconds * audioBuffer.sampleRate / offlineAudioData.fftSize)]
  }

  return null
}

const setupGain = (): void => {
  muteNode.connect(gainNode)
  gainNode.connect(audioContext.destination)
  gainNode.gain.value = 0.5  //reduce the volume to 50%
  refGainNodeValue = ref(gainNode.gain.value)
}

const setBufferNodeLoopTimes = () => {
  if (audioBufferSourceNode) {
    audioBufferSourceNode.loopStart = loopStartTime.value
    audioBufferSourceNode.loopEnd = loopEndTime.value
  }
}

const trimAudio = (startTime: number, endTime: number) => {
  loopStartTime.value = startTime
  loopEndTime.value = endTime
  setBufferNodeLoopTimes()
  audioDurationSeconds.value = loopEndTime.value - loopStartTime.value
  seekAudio(0)
}

const setGainValue = (value: number) => {
  gainNode.gain.value = value
}

setupGain()

export default function useAudioEngine () {
  return {
    loadState: readonly(loadState),
    fileName: readonly(fileName),
    addAudioFile,
    loadAudioURL,
    isPlaying: readonly(isPlaying),
    playAudio,
    stopAudio,
    muteAudio,
    unmuteAudio,
    audioDurationSeconds,
    audioCurrentTimeSeconds,
    unlockAudioContext,
    audioBinary,
    unloadAudio,
    getAudioDataAtTime,
    getAudioContextCurrentTime,
    audioBackgroundIntensity: readonly(audioBackgroundIntensity),
    volumeGain: refGainNodeValue,
    seekAudio,
    loopEndTime,
    loopStartTime,
    audioBufferWaveformData,
    trimAudio,
    untrimmedAudioDurationSeconds,
    setGainValue
  }
}
